'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Link from 'next/link';
import Custom404 from '../../[...not_found]/404';
import { IntegrationChatbot } from '@/constants/IntegrationChatbot';
import Image from 'next/image';
import ReactHtmlParser from 'react-html-parser';
import CTABanner from '@/components/blocks/CTABanner';

interface PageContentProps {
  locale: string;
  name: string;
}

export default function PageContent({ locale, name }: PageContentProps) {
  const { t } = useTranslation(locale);

  const selectData = IntegrationChatbot.find((dr) => dr.name.toLocaleLowerCase() == name.toLocaleLowerCase());

  if (!selectData) {
    return <Custom404 locale={locale} />;
  }

  return (
    <div>
      <div className="tw-flex tw-flex-col ">
        <div className="tw-flex ">
          <div className="md:tw-flex tw-flex-col tw-w-[224px] md:tw-h-[calc(100svh-55px)] tw-hidden tw-p-5 tw-gap-2">
            {IntegrationChatbot.map((item, index) => (
              <Link
                href={`/${locale}/chatbot-integration/${item.name}`}
                className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer hover:tw-text-blue-800"
                key={index}
              >
                <Image
                  priority
                  quality={100}
                  src={item.image}
                  className="tw-w-5 tw-h-5"
                  alt={t(item.title)}
                  title={t(item.title)}
                  width={48}
                  height={48}
                />
                <div className=" tw-text-left tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                  {t(item.title)}
                </div>
              </Link>
            ))}
          </div>
          <div className="tw-flex tw-flex-col md:tw-p-8 tw-p-2">
            <Link href={`/${locale}/chatbot-integration`} className="tw-flex tw-gap-2">
              <div className="tw-cursor-pointer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20.9999 11H6.41394L11.7069 5.70697L10.2929 4.29297L2.58594 12L10.2929 19.707L11.7069 18.293L6.41394 13H20.9999V11Z"
                    fill="#9CA3AF"
                  />
                </svg>
              </div>
              <div className="tw-cursor-pointer tw-text-gray-600 tw-text-lg tw-font-bold font-sofiaSans tw-leading-7">
                {t('IntegrationsPage/back')}
              </div>
            </Link>
            <h1 className="tw-text-gray-900 tw-text-4xl tw-font-semibold font-sofiaSans tw-leading-10 tw-mt-9">
              {t(selectData.blogTitle)}
            </h1>
            <div className="tw-mt-9">
              <Image
                priority
                quality={100}
                className="tw-max-w-[150px]"
                src={selectData.blogImage}
                alt={t(selectData.blogTitle)}
                title={t(selectData.blogTitle)}
                width={427}
                height={513}
              />
            </div>

            {selectData.blogTitleLink != '' && (
              <div className="tw-mt-9">
                <div className="tw-h-10 px-5 tw-py-2 tw-bg-blue-600 tw-rounded-md tw-shadow tw-justify-center tw-items-center tw-inline-flex">
                  <Link href={selectData.blogLink} target="_blank">
                    <div className="Text tw-text-white tw-text-base tw-font-medium font-sofiaSans tw-leading-normal">
                      {t(selectData.blogTitleLink)}
                    </div>
                  </Link>
                </div>
              </div>
            )}

            <h2 className="tw-mt-9 tw-text-gray-900 tw-text-base tw-font-normal font-sofiaSans tw-leading-normal">
              {ReactHtmlParser(t(selectData.blogDescription))}
            </h2>

            {selectData.bannerImage && (
              <a href={selectData.bannerLink} target="_blank" className="tw-mt-4" rel="noreferrer">
                <Image
                  priority
                  quality={100}
                  src={selectData.bannerImage}
                  className="tw-w-100 tw-hidden xl:tw-block object-contain border border-gray-300"
                  alt={t(selectData.blogTitle)}
                  title={t(selectData.blogTitle)}
                  width={728}
                  height={90}
                />
                <Image
                  priority
                  quality={100}
                  src={selectData.bannerImageSmall}
                  className="tw-w-full tw-block xl:tw-hidden object-contain border border-gray-300"
                  alt={t(selectData.blogTitle)}
                  title={t(selectData.blogTitle)}
                  width={1200}
                  height={628}
                />
              </a>
            )}

            <div className="tw-mt-9 tw-flex tw-flex-col ">
              {selectData.blogItems?.map((item, index) => (
                <div className="tw-flex tw-flex-col" key={index}>
                  <div className="md:tw-h-6 tw-justify-start lg:tw-items-center  tw-items-start tw-gap-2 tw-inline-flex tw-mt-8 tw-mb-8">
                    <div className="tw-w-6 tw-h-6 tw-py-0.5 tw-bg-blue-500 tw-rounded-full tw-border-4 tw-border-white tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                      <div className=" tw-self-stretch tw-text-center tw-text-white tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                        {index + 1}
                      </div>
                    </div>
                    <div className="tw-w-full Description tw-text-gray-900 tw-text-base tw-font-normal font-sofiaSans tw-leading-normal s">
                      {t(item.title)}
                    </div>
                  </div>
                  <div className="tw-px-3 tw-flex tw-gap-2 ">
                    <div className="Connector tw-w-0.5  tw-bg-gray-200 tw-hidden md:tw-block "></div>
                    <div className=" tw-py-5 tw-flex-col tw-gap-5">
                      {item.images.map((image, index) => (
                        <Image
                          priority
                          quality={100}
                          className={`object-contain border border-gray-300 tw-w-full ${index > 0 ? 'tw-mt-14' : ''}`}
                          src={image}
                          key={index}
                          alt={`LouiseBot_img_${index}`}
                          title={`LouiseBot_${index}`}
                          width={1545}
                          height={746}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <div className="tw-flex tw-flex-col tw-mt-5">
                {selectData.blogFooterItems?.map((item, index) => (
                  <div className="tw-flex tw-flex-col" key={index}>
                    <p>{t(item.title)}</p>

                    {item.images.map((image, index) => (
                      <Image
                        priority
                        quality={100}
                        src={image}
                        className="tw-w-full"
                        key={index}
                        alt={`Louisebot_${index}`}
                        title={`Louisebot_${index}`}
                        fill
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CTABanner
        title={t('Home/ReadyToGetStarted/cta-title')}
        description={t('Home/ReadyToGetStarted/cta-description')}
        cta={t('Home/ReadyToGetStarted/start-trial-aria')}
        ctaTarget="register"
        locale={locale}
      />
    </div>
  );
}
